import { mPerLat, mPerLon } from './geo';
import { haversine } from './model';

export const PIT_KEY = 'giss_pit_prices';
export const PIT_MATS = ['disposal', 'sand', 'gravel', 'asphalt', 'topsoil'];
export const DENSITY = { disposal: 1.8, sand: 1.6, gravel: 1.8, asphalt: 2.4, topsoil: 1.5 };
export const MAT_UNITS = { disposal: ['m3', 't'], sand: ['t', 'm3'], gravel: ['t', 'm3'], asphalt: ['m2', 'm3', 't'], topsoil: ['m3', 't'], custom: ['m3', 't', 'm2'] };
export const DN_LIST = [80, 100, 125, 150, 200, 250, 300, 400, 500, 600];
export const PIT_COLOR = '#f97316';

export const getGlobalPitPrices = () => {
  let p = {};
  try { p = JSON.parse(localStorage.getItem(PIT_KEY)) || {}; } catch { /* empty */ }
  return Object.fromEntries(PIT_MATS.map((k) => [k, { price: p[k]?.price ?? null, unit: p[k]?.unit || MAT_UNITS[k][0] }]));
};
// project-specific overrides of the active project (set by App)
let projPit = {};
export const setProjectPitPrices = (o) => { projPit = o || {}; };
export const getPitPrices = () => {
  const g = getGlobalPitPrices();
  return Object.fromEntries(PIT_MATS.map((k) => [k, projPit[k]?.price != null ? { price: projPit[k].price, unit: projPit[k].unit || g[k].unit, proj: true } : g[k]]));
};
export const setPitPrice = (k, patch) => {
  const p = getGlobalPitPrices();
  p[k] = { ...p[k], ...patch };
  localStorage.setItem(PIT_KEY, JSON.stringify(p));
  return p;
};

// local metric projection around the first point
const toXY = (pts) => {
  const [la0, lo0] = pts[0];
  const kx = mPerLon(la0);
  return pts.map(([la, lo]) => [(lo - lo0) * kx, (la - la0) * mPerLat(la0)]);
};
export const polyArea = (pts) => {
  if (pts.length < 3) return 0;
  const xy = toXY(pts);
  let s = 0;
  xy.forEach(([x, y], i) => { const [x2, y2] = xy[(i + 1) % xy.length]; s += x * y2 - x2 * y; });
  return Math.abs(s) / 2;
};
const longestEdge = (pts) => pts.reduce((m, p, i) => Math.max(m, haversine(p, pts[(i + 1) % pts.length])), 0);

export const rectFromCorners = ([a, b]) => [[a[0], a[1]], [a[0], b[1]], [b[0], b[1]], [b[0], a[1]]];
export function rectFromDims([lat, lon], len, wid) {
  const dLat = wid / 2 / mPerLat(lat);
  const dLon = len / 2 / (mPerLon(lat));
  return rectFromCorners([[lat - dLat, lon - dLon], [lat + dLat, lon + dLon]]);
}
const dist = (p, q) => { const [[x1, y1], [x2, y2]] = toXY([p, q]); return Math.hypot(x2 - x1, y2 - y1); };
const kxOf = (lat) => mPerLon(lat);
export function rectFromParams([lat, lon], len, wid, rot = 0) {
  const a = (rot * Math.PI) / 180, kx = kxOf(lat);
  return [[-1, -1], [1, -1], [1, 1], [-1, 1]].map(([sx, sy]) => {
    const x = (sx * len) / 2, y = (sy * wid) / 2;
    const xr = x * Math.cos(a) - y * Math.sin(a), yr = x * Math.sin(a) + y * Math.cos(a);
    return [lat + yr / mPerLat(lat), lon + xr / kx];
  });
}
// metric offset of q relative to p
export const offsetM = (p, q) => [(q[1] - p[1]) * kxOf(p[0]), (q[0] - p[0]) * mPerLat(p[0])];
export const moveBy = (pts, dLat, dLon) => pts.map(([la, lo]) => [la + dLat, lo + dLon]);
export function rotatePts(pts, c, deg) {
  const a = (deg * Math.PI) / 180, kx = kxOf(c[0]);
  return pts.map((p) => { const [x, y] = offsetM(c, p); return [c[0] + (x * Math.sin(a) + y * Math.cos(a)) / mPerLat(c[0]), c[1] + (x * Math.cos(a) - y * Math.sin(a)) / kx]; });
}
export const rectDims = (pts) => {
  const a = dist(pts[0], pts[1]), b = dist(pts[1], pts[2]);
  return { len: Math.max(a, b), wid: Math.min(a, b) };
};

export const pitCenter = (pit) => (pit.shape === 'circle' ? pit.center : [
  pit.pts.reduce((s, p) => s + p[0], 0) / pit.pts.length, pit.pts.reduce((s, p) => s + p[1], 0) / pit.pts.length]);

export function pitRing(pit, n = 36) {
  if (pit.shape !== 'circle') return pit.pts;
  const [la, lo] = pit.center;
  const kx = mPerLon(la);
  return Array.from({ length: n }, (_, i) => {
    const a = (i / n) * 2 * Math.PI;
    return [la + (pit.r * Math.sin(a)) / mPerLat(la), lo + (pit.r * Math.cos(a)) / kx];
  });
}

export const autoArea = (pit) => {
  if (pit.shape === 'circle') return Math.PI * pit.r * pit.r;
  if (pit.shape === 'rect' && pit.len && pit.wid) return pit.len * pit.wid;
  return polyArea(pit.pts);
};
const autoPipeLen = (pit) => (pit.shape === 'circle' ? 2 * pit.r : pit.shape === 'rect' && pit.len ? pit.len : longestEdge(pit.pts));

export function newPit({ shape, pts, center, r, name, reportId }) {
  const base = { id: `pit_${Date.now().toString(36)}${Math.random().toString(36).slice(2, 6)}`, name, reportId, shape, createdAt: Date.now(),
    areaOverride: null, depth: 2, dn: 150, odOverride: null, pipeLenOverride: null, bed: 0.1, cover: 0.1, sandOverride: null,
    fillOverride: null, surface: 'asphalt', surfThick: 0.1, qty: {}, dens: {}, custom: [], note: '', memos: [], media: [] };
  if (shape === 'circle') return { ...base, center, r };
  if (shape !== 'rect') return { ...base, pts };
  const d = rectDims(pts);
  const mid = [(pts[0][0] + pts[2][0]) / 2, (pts[0][1] + pts[2][1]) / 2];
  return { ...base, ...d, center: mid, rot: 0, pts: rectFromParams(mid, d.len, d.wid, 0) };
}

// DIN 4124: up to 1.25 m vertical walls allowed; deeper pits need a slope (angle by soil) or shoring
export const SOIL_ANGLE = { nc: 45, coh: 60, rock: 80 };
export const SHORE_DEPTH = 1.25;
const perimOf = (pit, area) => (pit.shape === 'circle' && +pit.dia ? Math.PI * +pit.dia : +pit.len && +pit.wid ? 2 * (+pit.len + +pit.wid) : 4 * Math.sqrt(area));
export function slopeOf(pit, area, depth) {
  const ang = SOIL_ANGLE[pit.soil];
  if (!ang || depth <= SHORE_DEPTH || area <= 0) return { extra: 0, angle: ang || null, top: area };
  const w = depth / Math.tan((ang * Math.PI) / 180), top = area + perimOf(pit, area) * w + Math.PI * w * w;
  return { extra: Math.max(0, (depth / 3) * (area + top + Math.sqrt(area * top)) - area * depth), angle: ang, top, w };
}

const num = (v, d) => (v === null || v === undefined || v === '' || Number.isNaN(+v) ? d : +v);

export function computePit(pit, prices = getPitPrices()) {
  const area = num(pit.areaOverride, autoArea(pit));
  const depth = num(pit.depth, 0);
  const od = num(pit.odOverride, num(pit.dn, 0) / 1000);
  const pipeLen = num(pit.pipeLenOverride, autoPipeLen(pit));
  const sandAuto = num(pit.bed, 0) + od + num(pit.cover, 0);
  const sandThick = num(pit.sandOverride, sandAuto);
  const surfThick = pit.surface === 'none' ? 0 : num(pit.surfThick, 0);
  const customThick = (pit.custom || []).reduce((s, c) => s + num(c.thick, 0), 0);
  const fillAuto = Math.max(0, depth - sandThick - surfThick - customThick);
  const fillThick = num(pit.fillOverride, fillAuto);
  const slope = slopeOf(pit, area, depth);
  const pipeVol = Math.PI * (od / 2) ** 2 * pipeLen;
  const q = pit.qty || {};
  const dens = (k) => num(pit.dens?.[k], DENSITY[k] ?? 1.8);
  const pr = { ...prices, ...(pit.prices || {}) };
  const row = (key, mat, name, thick, auto, m3, extra = {}) => {
    const v = num(q[key], m3);
    const d = extra.density ?? dens(mat);
    const tons = v * d;
    const { price, unit } = extra.price !== undefined ? extra : pr[mat] || {};
    const base = unit === 't' ? tons : unit === 'm2' ? area : v;
    return { key, mat, name, thick, auto, m3: v, autoM3: m3, density: d, t: tons, price: price ?? null, unit, cost: price != null && price !== '' ? base * +price : null };
  };
  const rows = [
    row('sand', 'sand', null, sandThick, sandAuto, Math.max(0, area * sandThick - pipeVol)),
    row('fill', 'gravel', null, fillThick, fillAuto, area * fillThick + slope.extra),
    ...(pit.custom || []).map((c) => row(c.id, 'custom', c.name, num(c.thick, 0), null, area * num(c.thick, 0), { density: num(c.density, 1.8), price: c.price ?? null, unit: c.unit || 'm3' })),
    ...(pit.surface === 'none' ? [] : [row('surface', pit.surface, null, surfThick, null, area * surfThick)]),
  ];
  const excav = row('excav', 'disposal', null, depth, null, area * depth + slope.extra);
  const total = [...rows, excav].reduce((s, r) => s + (r.cost || 0), 0);
  return { slope, shoreNeeded: depth > SHORE_DEPTH && !pit.soil, area, autoArea: autoArea(pit), depth, od, pipeLen, pipeVol, rows, excav, total, priced: [...rows, excav].some((r) => r.cost != null) };
}

export const layerName = (t, r) => r.name || t['pitL_' + r.key] || t['pitM_' + r.mat] || r.key;
export const unitLabel = (u) => ({ m3: 'm³', t: 't', m2: 'm²' }[u] || u);
export const f2 = (n, locale) => (n == null ? '–' : (+n).toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 }));

export function pitTotals(pits, prices) {
  const tot = { excav: 0, excavCost: 0, cost: 0, mats: {} };
  pits.forEach((p) => {
    const c = computePit(p, prices);
    tot.excav += c.excav.m3; tot.excavCost += c.excav.cost || 0; tot.cost += c.total;
    c.rows.forEach((r) => {
      const k = r.name || r.key;
      tot.mats[k] = tot.mats[k] || { row: r, m3: 0, t: 0, cost: 0 };
      tot.mats[k].m3 += r.m3; tot.mats[k].t += r.t; tot.mats[k].cost += r.cost || 0;
    });
  });
  return tot;
}

export function pitOverview(pits, reports = []) {
  const rows = pits.map((p) => {
    const c = computePit(p);
    const g = (k) => c.rows.find((r) => r.key === k) || null;
    const ri = reports.findIndex((x) => x.id === p.reportId);
    return { p, c, sand: g('sand'), fill: g('fill'), surf: g('surface'), rep: ri >= 0 ? `#${ri + 1}` : '–' };
  });
  return { rows, tot: pitTotals(pits), priced: rows.some((r) => r.c.priced) };
}
